import { useCallback, useEffect, useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, getToken } from "../lib/api";
import { Field, PageHeader, Panel } from "../components/common";
import { useMapMessages } from "./Marina";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BASE = process.env.REACT_APP_BACKEND_URL;

function MoliManager() {
  const [piers, setPiers] = useState([]);
  const [pier, setPier] = useState("");
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ numero: "", dim: "" });
  useEffect(() => { api("/marina/pier-list").then((d) => setPiers(d.piers || [])).catch(() => toast.error("Errore caricamento pontili")); }, []);
  const load = useCallback(() => { if (pier) api(`/marina/berths-by-pier?pontile=${encodeURIComponent(pier)}`).then((d) => setItems(d.items || [])).catch(() => toast.error("Errore caricamento posti")); }, [pier]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!f.numero.trim()) return toast.error("Inserisci il numero del posto");
    try { await api("/marina/berths", { method: "POST", body: { pontile: pier, numero: f.numero.trim(), dimensione: f.dim.trim() } }); toast.success("Posto barca aggiunto"); setF({ numero: "", dim: "" }); load(); } catch (e) { toast.error(e.message); }
  };
  const del = async (r) => {
    if (!window.confirm(`Eliminare il posto ${r.pontile} - ${r.numero}?`)) return;
    try { await api(`/marina/berths/${r.id}`, { method: "DELETE" }); toast.success("Posto eliminato"); load(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Panel className="space-y-4">
      <h2 className="font-display font-bold text-lg text-[var(--navy)]">Pontili e posti barca</h2>
      <Field label="Pontile"><Select value={pier} onValueChange={setPier}><SelectTrigger data-testid="moli-pier-select"><SelectValue placeholder="Seleziona pontile..." /></SelectTrigger><SelectContent>{piers.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></Field>
      {!pier ? <p className="text-sm text-slate-500">Seleziona un pontile per gestire i posti barca.</p> : <>
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <Field label="Numero posto"><Input value={f.numero} onChange={(e) => setF({ ...f, numero: e.target.value })} placeholder="es. 21" data-testid="moli-numero-input" /></Field>
          <Field label="Lunghezza (m)"><Input value={f.dim} onChange={(e) => setF({ ...f, dim: e.target.value })} placeholder="es. 12 m" data-testid="moli-dim-input" /></Field>
          <Button onClick={add} className="rounded-full bg-[var(--teal)]" data-testid="moli-add-button">Salva posto</Button>
        </div>
        {items.length === 0 ? <p className="text-sm text-slate-500">Nessun posto per questo pontile.</p> : (
          <div className="max-h-80 overflow-y-auto divide-y border rounded-xl" data-testid="moli-list">{items.map((r) => (
            <div key={r.id} className="flex items-center justify-between px-4 py-2 text-sm"><span>{r.pontile} - {r.numero} {r.dimensione && <span className="text-slate-400">· {r.dimensione}</span>} {r.on_map && <span className="tag tag-teal ml-1">Sulla mappa</span>}</span>
              <button onClick={() => del(r)} className="text-slate-400 hover:text-[var(--red)]" data-testid={`moli-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button></div>))}
          </div>)}
      </>}
    </Panel>
  );
}

export default function MarinaAdmin() {
  const [presence, setPresence] = useState(true);
  const [key, setKey] = useState(0);
  const url = useMemo(() => `${BASE}/api/marina/map-preview?t=${encodeURIComponent(getToken() || "")}&admin=1&v=${Date.now()}_${key}`, [key]);
  useEffect(() => { api("/marina/config").then((d) => setPresence(d.presence_enabled !== false)).catch(() => {}); }, []);

  const onMsg = useCallback(async (msg) => {
    if (msg.type === "error") return;
    if (msg.type === "info") toast.info(msg.message || "");
    if (msg.type === "save") {
      try { await api("/marina/features", { method: "PUT", body: { features: msg.features || [] } }); toast.success("Mappa salvata"); setKey((k) => k + 1); } catch (e) { toast.error(e.message || "Errore nel salvataggio"); }
    }
  }, []);
  useMapMessages(onMsg);

  const togglePresence = async (v) => {
    try { await api("/marina/config", { method: "PUT", body: { presence_enabled: v } }); setPresence(v); toast.success(v ? "Presenze abilitate" : "Presenze disabilitate"); } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <PageHeader subtitle="Disegna e collega gli elementi, poi Salva" title="Mappa Admin" actions={<label className="flex items-center gap-3 panel !py-2 !px-4 text-sm font-medium">Abilita Presenze<Switch checked={presence} onCheckedChange={togglePresence} data-testid="marina-presence-switch" /></label>} />
      <div className="rounded-2xl overflow-hidden border bg-white h-[72vh] mb-8">
        <iframe key={url} src={url} title="Mappa admin" className="w-full h-full border-0" data-testid="marina-admin-iframe" />
      </div>
      <MoliManager />
    </div>
  );
}
